import { AlertTriangle, MessageSquareWarning, Info } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import { cn } from "@/v2/lib/utils";

const variants = {
	error: {
		container: "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30",
		icon: "text-red-600 dark:text-red-400",
		title: "text-red-800 dark:text-red-300",
		body: "text-red-700 dark:text-red-400/80",
		Icon: AlertTriangle,
	},
	warning: {
		container: "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30",
		icon: "text-amber-600 dark:text-amber-400",
		title: "text-amber-800 dark:text-amber-300",
		body: "text-amber-700 dark:text-amber-400/80",
		Icon: MessageSquareWarning,
	},
	info: {
		container: "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/30",
		icon: "text-blue-600 dark:text-blue-400",
		title: "text-blue-800 dark:text-blue-300",
		body: "text-blue-700 dark:text-blue-400/80",
		Icon: Info,
	},
} as const;

interface BannerProps {
	variant: keyof typeof variants;
	title: string;
	children: React.ReactNode;
	action?: {
		label: string;
		onClick: () => void;
		loading?: boolean;
	};
	className?: string;
}

export function Banner({ variant, title, children, action, className }: BannerProps) {
	const v = variants[variant];

	return (
		<div className={cn("rounded-md border p-3", v.container, className)}>
			<div className="flex items-start gap-2">
				<v.Icon className={cn("h-4 w-4 mt-0.5 shrink-0", v.icon)} />
				<div className="flex-1 min-w-0">
					<p className={cn("text-sm font-medium", v.title)}>{title}</p>
					<div className={cn("mt-1 text-sm", v.body)}>{children}</div>
				</div>
				{action && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={action.onClick}
						disabled={action.loading}
						className="shrink-0"
					>
						{action.loading ? "..." : action.label}
					</Button>
				)}
			</div>
		</div>
	);
}
